import { ErrorWithRetry, type Guard, sessionAtom, usePrevious } from '@capsicum/common';
import { privateKeyHexAtom, useMe } from '@capsicum/socios-core';
import { useAtomValue } from 'jotai';
// eslint-disable-next-line no-restricted-imports
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback } from 'react';

import { useDeeplinkRedirectGuard } from '../hooks';

export const UserGuard: Guard = () => {
	const { isMeLoading, meError } = useMe();
	const privateKey = useAtomValue(privateKeyHexAtom);
	const sessionTokens = useAtomValue(sessionAtom);
	const { setDeeplinkRedirect } = useDeeplinkRedirectGuard();

	const { replace } = useRouter();
	const searchParams = useSearchParams();

	const previousSessionToken = usePrevious(sessionTokens);

	const isAuthenticated = !!sessionTokens?.authToken;

	const onDenied = useCallback(() => {
		// Throwing an error to avoid an endless loop
		const urlCallback = searchParams.get('url_callback');

		if (isAuthenticated && !urlCallback) {
			throw new ErrorWithRetry({
				errorTitle: 'error.default',
				loggable: true,
				message: 'Error retrieving user data'
			});
		}

		if (!previousSessionToken) {
			setDeeplinkRedirect();
		}
		if (privateKey) {
			replace('/export-and-close-account');

			return;
		}
		replace('/login?' + searchParams.toString());
	}, [isAuthenticated, previousSessionToken, replace, setDeeplinkRedirect, privateKey]);

	const getStatus = () => {
		if (sessionTokens && isMeLoading) return 'loading';
		if (!!meError || !isAuthenticated || !sessionTokens) {
			return 'denied';
		}

		return 'allowed';
	};

	return {
		metadata: { isAuthenticated, loading: isMeLoading, reason: 'User is not authenticated' },
		onDenied,
		status: getStatus()
	};
};
